import React, { useEffect, useMemo, useState } from "react";
import { adminApi, API_BASE } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Download, ChevronDown, ChevronRight } from "lucide-react";

const TYPE_LABELS = {
    booking: "Booking & Dining",
    wedding: "Wedding & Banquet",
    membership: "Membership",
};

const STATUS_OPTIONS = ["new", "contacted", "closed"];

export default function Leads() {
    const [type, setType] = useState("booking");
    const [items, setItems] = useState([]);
    const [expanded, setExpanded] = useState({});

    const load = () => {
        adminApi.get("/api/admin/leads", { params: { type } }).then((r) => setItems(r.data.items));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    const updateStatus = async (id, status) => {
        await adminApi.patch(`/api/admin/leads/${id}`, { status });
        load();
    };

    const exportUrl = `${API_BASE}/api/admin/leads/export.csv?type=${type}`;

    return (
        <div className="p-10 max-w-7xl">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">
                    Leads & Bookings
                </div>
                <h1 className="font-heading text-5xl text-orchid-700">Pipeline</h1>
                <p className="text-stone-600 mt-2">
                    Captured by the Concierge — ready for your team to action.
                </p>
            </div>

            <div className="flex items-center justify-between mb-6">
                <div className="flex gap-1 bg-white border border-stone-200 rounded-md p-1">
                    {Object.entries(TYPE_LABELS).map(([k, v]) => (
                        <button
                            key={k}
                            onClick={() => setType(k)}
                            data-testid={`leads-tab-${k}`}
                            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                                type === k
                                    ? "bg-orchid-700 text-white"
                                    : "text-stone-700 hover:bg-stone-50"
                            }`}
                        >
                            {v}
                        </button>
                    ))}
                </div>

                <a
                    href={exportUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="leads-export"
                    className="flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
                >
                    <Download size={15} /> Export CSV
                </a>
            </div>

            <div className="space-y-2">
                {items.map((lead) => {
                    const open = !!expanded[lead.id];
                    const f = lead.fields || {};
                    return (
                        <div
                            key={lead.id}
                            data-testid={`lead-${lead.id}`}
                            className="bg-white border border-stone-200 rounded-lg overflow-hidden"
                        >
                            <button
                                onClick={() =>
                                    setExpanded({ ...expanded, [lead.id]: !open })
                                }
                                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-stone-50"
                            >
                                <div className="flex items-center gap-4 min-w-0">
                                    {open ? (
                                        <ChevronDown size={18} className="text-stone-400" />
                                    ) : (
                                        <ChevronRight size={18} className="text-stone-400" />
                                    )}
                                    <div>
                                        <div className="font-medium text-stone-900">
                                            {f.name || "—"}
                                            {f.plan && (
                                                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gold-500/15 text-gold-700">
                                                    {f.plan}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-stone-500 mt-0.5">
                                            {f.mobile} · {f.email}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    {lead.is_member && (
                                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-orchid-700/10 text-orchid-700">
                                            Member
                                        </span>
                                    )}
                                    {lead.has_offer && (
                                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-gold-500/15 text-gold-700">
                                            Offer
                                        </span>
                                    )}
                                    <span className="text-xs text-stone-500">
                                        {formatDateTime(lead.created_at)}
                                    </span>
                                    <select
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => updateStatus(lead.id, e.target.value)}
                                        value={lead.status}
                                        data-testid={`lead-status-${lead.id}`}
                                        className={`text-xs px-2 py-1 rounded border ${
                                            lead.status === "new"
                                                ? "bg-orchid-500/10 border-orchid-500/30 text-orchid-600"
                                                : lead.status === "contacted"
                                                  ? "bg-gold-500/15 border-gold-500/30 text-gold-700"
                                                  : "bg-stone-100 border-stone-200 text-stone-600"
                                        }`}
                                    >
                                        {STATUS_OPTIONS.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </button>
                            {open && (
                                <div className="px-5 pb-5 pt-1 border-t border-stone-100 bg-stone-50/50">
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm pt-4">
                                        {Object.entries(f).map(([k, v]) => (
                                            <div key={k}>
                                                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                                                    {k.replace(/_/g, " ")}
                                                </div>
                                                <div className="text-stone-900">{String(v)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                {items.length === 0 && (
                    <div className="text-center text-stone-500 py-16 bg-white border border-stone-200 rounded-lg">
                        No {TYPE_LABELS[type].toLowerCase()} leads yet.
                    </div>
                )}
            </div>
        </div>
    );
}
